<template>
  <div class="page-container">
    <div class="page-header">
      <h1 class="page-title">{{ pageTitle }}</h1>
      <div class="header-actions">
        <button class="btn btn-secondary" :disabled="loading" @click="fetchTicket(true)">
          <i class="ri-refresh-line"></i>
          重新整理
        </button>
        <router-link
          v-if="ticket && ticket.status !== 'sold_out'"
          :to="{ name: 'ClientWixtarTicketDispatch', params: { ticketId } }"
          class="btn btn-primary"
        >
          <i class="ri-send-plane-line"></i>
          派券給會員
        </router-link>
        <router-link :to="{ name: 'ClientWixtarTicketList' }" class="btn btn-secondary">
          <i class="ri-arrow-left-line"></i>
          返回列表
        </router-link>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchTicket()">重試</button>
    </div>

    <div v-else-if="ticket" class="detail-container">
      <div class="detail-section">
        <h3 class="section-title">票券資訊</h3>
        <div class="info-grid">
          <div class="info-item">
            <label>票券名稱</label>
            <div>{{ ticket.ticketName }}</div>
          </div>
          <div class="info-item">
            <label>票券 ID（派券用的 ticket_no）</label>
            <div class="mono">{{ ticket.ticketId }}</div>
          </div>
          <div class="info-item">
            <label>狀態</label>
            <div>
              <span :class="statusBadgeClass(ticket.status)">{{ statusText(ticket.status) }}</span>
            </div>
          </div>
          <div class="info-item">
            <label>總量 / 剩餘 / 已派發</label>
            <div>
              {{ formatQuantity(ticket.quantity) }}
              /
              <strong :class="{ 'text-danger': ticket.availableQuantity === 0 }">
                {{ formatQuantity(ticket.availableQuantity) }}
              </strong>
              /
              {{ formatQuantity(ticket.issuedQuantity) }}
            </div>
          </div>
          <div class="info-item">
            <label>有效期限</label>
            <div>
              {{ ticket.validFrom || '未提供' }} ～ {{ ticket.validTo || '未提供' }}
            </div>
          </div>
          <div class="info-item">
            <label>票券說明</label>
            <div>{{ ticket.ticketDescription || '未提供' }}</div>
          </div>
        </div>
      </div>

      <div class="detail-section">
        <h3 class="section-title">所屬活動</h3>
        <div class="info-grid">
          <div class="info-item">
            <label>活動名稱</label>
            <div>{{ ticket.activitySubject || '未提供' }}</div>
          </div>
          <div class="info-item">
            <label>活動 ID</label>
            <div class="mono">{{ ticket.activityId || '未提供' }}</div>
          </div>
          <div class="info-item">
            <label>活動說明</label>
            <div>{{ ticket.activityDescription || '未提供' }}</div>
          </div>
        </div>
      </div>

      <div v-if="ticket.siblingTickets?.length" class="detail-section">
        <h3 class="section-title">同活動的其他票券</h3>
        <div class="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>票券名稱</th>
                <th>票券 ID</th>
                <th>剩餘</th>
                <th>狀態</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="sibling in ticket.siblingTickets" :key="sibling.ticketId">
                <td>{{ sibling.ticketName }}</td>
                <td class="mono">{{ sibling.ticketId }}</td>
                <td>{{ formatQuantity(sibling.availableQuantity) }}</td>
                <td>
                  <span :class="statusBadgeClass(sibling.status)">{{ statusText(sibling.status) }}</span>
                </td>
                <td>
                  <router-link
                    :to="{ name: 'ClientWixtarTicketDetail', params: { ticketId: sibling.ticketId } }"
                    class="btn btn-sm btn-secondary"
                  >
                    查看
                  </router-link>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="detail-section">
        <h3 class="section-title">
          Wixtar 原始欄位
          <small>Wixtar 回傳的欄位會隨版本增減，此處完整列出供比對</small>
        </h3>
        <pre class="raw-json">{{ JSON.stringify(ticket.raw, null, 2) }}</pre>
        <p v-if="ticket.fetchedAt" class="data-time-hint">
          <i class="ri-time-line"></i>
          資料取自 Wixtar 於 {{ formatDateTime(ticket.fetchedAt) }}
        </p>
      </div>
    </div>

  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { wixtarService } from '@/api/modules/wixtar.js';

export default {
  name: 'WixtarTicketDetail',
  setup() {
    const route = useRoute();
    const { pageTitle } = usePageTitle('Wixtar 票券詳情');

    const ticketId = route.params.ticketId;
    const ticket = ref(null);
    const loading = ref(false);
    const error = ref(null);

    const fetchTicket = async (refresh = false) => {
      loading.value = true;
      error.value = null;

      try {
        const params = refresh ? { refresh: true } : {};
        const response = await wixtarService.getTicketCatalogItem(ticketId, params);

        if (response && response.success && response.data) {
          ticket.value = response.data;
        } else {
          error.value = response?.message || '取得 Wixtar 票券詳情失敗';
        }
      } catch (err) {
        console.error('取得 Wixtar 票券詳情失敗:', err);
        error.value = err.message || '取得 Wixtar 票券詳情失敗';
      } finally {
        loading.value = false;
      }
    };

    const formatQuantity = (value) => (value === null || value === undefined ? '未提供' : value);

    const statusText = (status) => {
      if (status === 'available') return '可派發';
      if (status === 'sold_out') return '已發完';
      return '數量未提供';
    };

    const statusBadgeClass = (status) => {
      if (status === 'available') return 'badge badge-success';
      if (status === 'sold_out') return 'badge badge-secondary';
      return 'badge badge-warning';
    };

    const formatDateTime = (value) => (value ? new Date(value).toLocaleString('zh-TW') : '-');

    onMounted(() => {
      fetchTicket();
    });

    return {
      pageTitle,
      ticketId,
      ticket,
      loading,
      error,
      fetchTicket,
      formatQuantity,
      statusText,
      statusBadgeClass,
      formatDateTime
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_detail-common.scss';

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0 0 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.section-title small {
  display: block;
  font-size: 12px;
  font-weight: 400;
  color: #6c757d;
  margin-top: 4px;
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 14px;
}

.text-danger {
  color: #dc3545;
}

.table-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.table-wrapper table {
  width: 100%;
  min-width: 600px;
  border-collapse: collapse;
}

.table-wrapper th,
.table-wrapper td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid #e9ecef;
  font-size: 14px;
}

.table-wrapper thead {
  background: #f8f9fa;
}

.btn-sm {
  padding: 4px 10px;
  font-size: 13px;
}

.raw-json {
  margin: 0;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 6px;
  font-size: 12px;
  line-height: 1.6;
  overflow-x: auto;
  max-height: 420px;
}

.data-time-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #6c757d;
  margin: 12px 0 0;
}
</style>
